import React, { useEffect, useState } from "react";
import { Apple, Smartphone, Download, CheckCircle2, Shield, Zap } from "lucide-react";
import { toast } from "sonner";

/**
 * Full-width "Download App" section for the bottom of the Home page.
 * Offers both Apple (iPhone/iPad) and Google (Android) direct-install options.
 * Uses the PWA `beforeinstallprompt` for Android/Chrome and falls back to
 * a platform-specific instructions modal (iOS Safari "Add to Home Screen").
 */
export default function DownloadAppSection() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [helpFor, setHelpFor] = useState(null); // "ios" | "android" | null

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); toast.success("Tap In TV installed!"); };
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", onInstalled);
    if (window.matchMedia?.("(display-mode: standalone)")?.matches) setInstalled(true);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerAndroid = async () => {
    if (installed) { toast.success("Already installed on this device"); return; }
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice.outcome === "accepted") toast.success("Installing Tap In TV…");
      setDeferred(null);
    } else {
      setHelpFor("android");
    }
  };

  const triggerIos = () => {
    if (installed) { toast.success("Already installed on this device"); return; }
    setHelpFor("ios");
  };

  return (
    <section data-testid="download-app-section" className="px-3 sm:px-6 md:px-10 py-14 sm:py-20 mt-10">
      <div className="max-w-5xl mx-auto tv-glass rounded-3xl border border-white/10 overflow-hidden relative">
        <div className="absolute inset-0 bg-gradient-to-br from-tv-accent/10 via-transparent to-tv-brand/15 pointer-events-none" />
        <div className="relative p-6 sm:p-10 md:p-14 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2">
              <Download className="w-3.5 h-3.5" /> Get the app
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white tracking-tighter mt-3 leading-[1.05]">
              Take Tap IN TV<br /><span className="text-tv-accent">on the go.</span>
            </h2>
            <p className="text-white/75 mt-4 text-sm md:text-base max-w-md">
              Installs <span className="text-white font-semibold">directly on your device</span> — no app store, no review queue, no download limits. It runs full-screen like a native app with offline-ready caching and push-notification support.
            </p>

            <ul className="mt-5 space-y-2 text-white/80 text-sm">
              <li data-testid="download-feat-direct" className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-tv-accent shrink-0" /> Direct install straight to your home screen
              </li>
              <li data-testid="download-feat-secure" className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-tv-accent shrink-0" /> Same secure sign-in as the web app
              </li>
              <li data-testid="download-feat-fast" className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-tv-accent shrink-0" /> Launches instantly — zero bundle download
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              data-testid="download-ios-btn"
              onClick={triggerIos}
              className="group flex items-center gap-4 bg-white hover:bg-white/95 text-black rounded-2xl px-5 py-4 transition shadow-xl shadow-black/30"
            >
              <Apple className="w-9 h-9 shrink-0" />
              <div className="text-left flex-1">
                <div className="text-[10px] uppercase tracking-widest text-black/60 font-bold">Download for</div>
                <div className="font-display font-black text-xl leading-tight">iPhone &amp; iPad</div>
                <div className="text-[11px] text-black/60 mt-0.5">Add to Home Screen · Safari</div>
              </div>
              <Download className="w-5 h-5 opacity-60 group-hover:opacity-100 transition" />
            </button>

            <button
              type="button"
              data-testid="download-android-btn"
              onClick={triggerAndroid}
              className="group flex items-center gap-4 bg-gradient-to-br from-emerald-500 to-emerald-600 hover:brightness-110 text-white rounded-2xl px-5 py-4 transition shadow-xl shadow-emerald-500/20"
            >
              <Smartphone className="w-9 h-9 shrink-0" />
              <div className="text-left flex-1">
                <div className="text-[10px] uppercase tracking-widest text-white/80 font-bold">Download for</div>
                <div className="font-display font-black text-xl leading-tight">Android</div>
                <div className="text-[11px] text-white/80 mt-0.5">{deferred ? "One-tap install · Chrome" : "Add to Home Screen · Chrome"}</div>
              </div>
              <Download className="w-5 h-5 opacity-80 group-hover:opacity-100 transition" />
            </button>

            <p className="text-[11px] text-white/50 text-center mt-1" data-testid="download-disclaimer">
              Installs as a Progressive Web App — no app-store account required. You&apos;ll see the Tap IN TV icon on your home screen within seconds.
            </p>
          </div>
        </div>
      </div>

      {helpFor && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setHelpFor(null)}>
          <div data-testid="download-help-modal" className="tv-glass rounded-2xl w-full max-w-md p-6 border border-white/10" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-widest text-tv-accent font-bold flex items-center gap-2">
              {helpFor === "ios" ? <Apple className="w-4 h-4" /> : <Smartphone className="w-4 h-4" />}
              Install Tap IN TV
            </div>
            <h3 className="font-display font-bold text-white text-2xl mt-1">
              {helpFor === "ios" ? "On iPhone or iPad" : "On Android"}
            </h3>
            <p className="text-white/60 text-sm mt-2">Installs directly to your device — no app store needed.</p>

            {helpFor === "ios" ? (
              <ol data-testid="ios-install-steps" className="mt-4 space-y-3 text-sm text-white/85 list-decimal list-inside">
                <li>Open this page in <b className="text-white">Safari</b> (not Chrome).</li>
                <li>Tap the <span className="text-tv-accent font-semibold">Share</span> icon at the bottom of the screen.</li>
                <li>Scroll down and tap <span className="text-tv-accent font-semibold">Add to Home Screen</span>.</li>
                <li>Tap <span className="text-tv-accent font-semibold">Add</span> — the Tap IN TV icon lands on your home screen.</li>
              </ol>
            ) : (
              <ol data-testid="android-install-steps" className="mt-4 space-y-3 text-sm text-white/85 list-decimal list-inside">
                <li>Open this page in <b className="text-white">Chrome</b>.</li>
                <li>Tap the <span className="text-tv-accent font-semibold">⋮ menu</span> in the top-right corner.</li>
                <li>Tap <span className="text-tv-accent font-semibold">Install app</span> or <span className="text-tv-accent font-semibold">Add to Home screen</span>.</li>
                <li>Confirm — the app installs like a native Android app.</li>
              </ol>
            )}

            <button
              data-testid="download-help-close"
              onClick={() => setHelpFor(null)}
              className="mt-6 w-full bg-tv-accent text-black font-bold py-2.5 rounded-full"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
